import { createEffect, createSignal, onCleanup } from "solid-js";

export interface TooltipPosition {
  position: "top" | "bottom" | "left" | "right";
  alignment: "start" | "center" | "end";
}

export interface ViewportAwareTooltipOptions {
  /** Tooltip width in pixels */
  tooltipWidth: number;
  /** Tooltip height in pixels */
  tooltipHeight: number;
  /** Margin from viewport edge in pixels */
  margin?: number;
  /** Preferred position if space allows */
  preferredPosition?: "top" | "bottom" | "left" | "right";
}

/**
 * Hook for viewport-aware tooltip positioning
 * Automatically adjusts tooltip position to avoid viewport edges
 */
export const useViewportAwareTooltip = (
  triggerRef: () => HTMLElement | undefined,
  isVisible: () => boolean,
  options: ViewportAwareTooltipOptions,
) => {
  const [position, setPosition] = createSignal<TooltipPosition>({
    position: options.preferredPosition || "bottom",
    alignment: "center",
  });

  const margin = options.margin || 8;

  const calculatePosition = (): TooltipPosition => {
    const trigger = triggerRef();
    if (!trigger) {
      return { position: "bottom", alignment: "center" };
    }

    const rect = trigger.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Calculate available space in each direction
    const spaceAbove = rect.top;
    const spaceBelow = viewport.height - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewport.width - rect.right;

    // Determine vertical position
    let verticalPos: "top" | "bottom";
    if (options.preferredPosition === "top" && spaceAbove >= options.tooltipHeight + margin) {
      verticalPos = "top";
    } else if (options.preferredPosition === "bottom" && spaceBelow >= options.tooltipHeight + margin) {
      verticalPos = "bottom";
    } else {
      // Auto-choose based on available space
      verticalPos = spaceBelow >= spaceAbove ? "bottom" : "top";
    }

    // Determine horizontal alignment to avoid viewport edges
    let alignment: "start" | "center" | "end" = "center";
    const tooltipLeft = rect.left + rect.width / 2 - options.tooltipWidth / 2;
    const tooltipRight = tooltipLeft + options.tooltipWidth;

    if (tooltipLeft < margin) {
      alignment = "start"; // Align to left edge of trigger
    } else if (tooltipRight > viewport.width - margin) {
      alignment = "end"; // Align to right edge of trigger
    }

    // For very constrained horizontal space, consider side positioning
    if (options.tooltipWidth > viewport.width - 2 * margin) {
      // Tooltip is too wide, prefer top/bottom positioning
      return { position: verticalPos, alignment };
    }

    // Check if horizontal positioning would be better
    if (verticalPos === "bottom" && spaceBelow < options.tooltipHeight + margin) {
      if (spaceRight >= options.tooltipWidth + margin) {
        return { position: "right", alignment: "center" };
      } else if (spaceLeft >= options.tooltipWidth + margin) {
        return { position: "left", alignment: "center" };
      }
    }

    if (verticalPos === "top" && spaceAbove < options.tooltipHeight + margin) {
      if (spaceRight >= options.tooltipWidth + margin) {
        return { position: "right", alignment: "center" };
      } else if (spaceLeft >= options.tooltipWidth + margin) {
        return { position: "left", alignment: "center" };
      }
    }

    return { position: verticalPos, alignment };
  };

  // Update position when visibility changes or viewport changes
  createEffect(() => {
    if (!isVisible() || !triggerRef()) return;

    const updatePosition = () => {
      setPosition(calculatePosition());
    };

    // Update immediately if trigger is available
    if (triggerRef()) {
      updatePosition();
    }

    // Update on resize/scroll
    const handleUpdate = () => {
      if (isVisible() && triggerRef()) {
        updatePosition();
      }
    };

    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true); // Capture scroll events

    onCleanup(() => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    });
  });

  // Generate CSS classes based on position
  const getTooltipClasses = () => {
    const pos = position();
    const baseClasses = "absolute z-50 transition-all duration-200";

    switch (pos.position) {
      case "top":
        const topAlignment =
          pos.alignment === "start" ? "left-0" : pos.alignment === "end" ? "right-0" : "left-1/2 -translate-x-1/2";
        return `${baseClasses} bottom-full mb-2 ${topAlignment}`;

      case "bottom":
        const bottomAlignment =
          pos.alignment === "start" ? "left-0" : pos.alignment === "end" ? "right-0" : "left-1/2 -translate-x-1/2";
        return `${baseClasses} top-full mt-2 ${bottomAlignment}`;

      case "left":
        return `${baseClasses} right-full mr-2 top-1/2 -translate-y-1/2`;

      case "right":
        return `${baseClasses} left-full ml-2 top-1/2 -translate-y-1/2`;

      default:
        return `${baseClasses} top-full mt-2 left-1/2 -translate-x-1/2`;
    }
  };

  // Generate tail/arrow position
  const getTailClasses = () => {
    const pos = position();
    const baseClasses = "absolute w-4 h-4 transform rotate-45";

    switch (pos.position) {
      case "top":
        const topTailAlignment =
          pos.alignment === "start" ? "left-4" : pos.alignment === "end" ? "right-4" : "left-1/2 -translate-x-1/2";
        return `${baseClasses} top-full -mt-2 ${topTailAlignment}`;

      case "bottom":
        const bottomTailAlignment =
          pos.alignment === "start" ? "left-4" : pos.alignment === "end" ? "right-4" : "left-1/2 -translate-x-1/2";
        return `${baseClasses} bottom-full -mb-2 ${bottomTailAlignment}`;

      case "left":
        return `${baseClasses} left-full -ml-2 top-1/2 -translate-y-1/2`;

      case "right":
        return `${baseClasses} right-full -mr-2 top-1/2 -translate-y-1/2`;

      default:
        return `${baseClasses} bottom-full -mb-2 left-1/2 -translate-x-1/2`;
    }
  };

  return {
    position,
    getTooltipClasses,
    getTailClasses,
  };
};
